/**
 * Collates the chat view's derived hooks (split view, send handler, stop,
 * attachments, auto-effects). Kept separate from useChatViewInit to stay
 * under the per-function complexity ceiling.
 */

import type { UseAgentsWebsocketConfigReturn } from '@/hooks/use-agents-websocket-config.hook'
import { useAttachmentDownload } from '@/hooks/use-attachment-download.hook'
import { useAutoOpenReport } from './use-auto-open-report.hook'
import { useChatAutoSendEffects } from './use-chat-auto-send-effects.hook'
import type { UseChatInputReturn } from './use-chat-input.hook'
import type { UseChatMessagesReturn } from './use-chat-messages.hook'
import { useChatSendHandler } from './use-chat-send-handler.hook'
import { useChatSplitView } from './use-chat-split-view.hook'
import { useChatStopExecution } from './use-chat-stop-execution.hook'
import { useWebsocketInit } from './use-websocket-init.hook'

interface UseChatSideEffectsParams {
  conversationId: string | null
  agencySlug: string | null
  input: UseChatInputReturn
  messages: UseChatMessagesReturn
  websocket: UseAgentsWebsocketConfigReturn
}

export const useChatSideEffects = ({
  conversationId,
  agencySlug,
  input,
  messages,
  websocket,
}: UseChatSideEffectsParams) => {
  const splitViewActions = useChatSplitView({ onSendMessage: messages.sendMessage })
  const sendHandler = useChatSendHandler({ input, messages })
  const stopExecution = useChatStopExecution({ conversationId })
  const attachmentDownload = useAttachmentDownload()
  useWebsocketInit(websocket)
  const { isPlanRestricted } = useChatAutoSendEffects({
    input,
    messages,
    sendHandler,
    agencySlug: agencySlug ?? undefined,
  })
  useAutoOpenReport({ onReportClick: splitViewActions.handleReportClick })

  return {
    splitViewActions,
    sendHandler,
    stopExecution,
    attachmentDownload,
    isPlanRestricted,
  }
}
